import { can, type Permission, type SessionUser } from '@/app/store/session.store';
import type { IconName } from '@/shared/ui/atoms/Icon';

export type NavKey =
  | 'dashboard'
  | 'board'
  | 'projects'
  | 'team'
  | 'reports'
  | 'analytics'
  | 'calendar'
  | 'messages'
  | 'tenants'
  | 'users'
  | 'invitations'
  | 'settings';

/** `key` resolves to `nav.<key>` (menu) and `nav.rail.<key>` (rail tooltip) translations. */
export type NavItem = {
  key: NavKey;
  to: string;
  icon: IconName;
  rail?: boolean;
};

/** Menu entries in the order of the design; the rail shows the first five. */
export const NAV: NavItem[] = [
  { key: 'dashboard', to: '/dashboard', icon: 'dashboard', rail: true },
  { key: 'board', to: '/board', icon: 'tasks', rail: true },
  { key: 'projects', to: '/projects', icon: 'projects', rail: true },
  { key: 'team', to: '/team', icon: 'team', rail: true },
  { key: 'reports', to: '/reports', icon: 'reports', rail: true },
  { key: 'analytics', to: '/analytics', icon: 'analytics' },
  { key: 'calendar', to: '/calendar', icon: 'calendar' },
  { key: 'messages', to: '/messages', icon: 'messages' }
];

export const PLATFORM_NAV: NavItem[] = [
  { key: 'tenants', to: '/platform/tenants', icon: 'building', rail: true },
  { key: 'users', to: '/platform/users', icon: 'team', rail: true },
  {
    key: 'invitations',
    to: '/platform/invitations',
    icon: 'userPlus',
    rail: true
  },
  { key: 'settings', to: '/platform/settings', icon: 'settings', rail: true }
];

/** A settings section, shown as a sub-item of "Settings" in the sidebar (`?section=<id>`). */
export type SettingsSection = {
  id: string;
  label: string;
  /** Shown only to users with this permission. */
  permission?: Permission;
};

/** The organisation's settings; the first is the default. */
export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  { id: 'profile', label: 'pages.settings.profile' },
  { id: 'preferences', label: 'pages.settings.nav.preferences' },
  { id: 'mailbox', label: 'pages.settings.nav.mailbox' },
  { id: 'details', label: 'pages.settings.workspace', permission: 'organization.manage' },
  { id: 'tax', label: 'pages.settings.nav.tax', permission: 'organization.manage' },
  { id: 'members', label: 'pages.settings.members', permission: 'members.manage' },
  { id: 'invitations', label: 'pages.settings.nav.invitations', permission: 'members.manage' },
  { id: 'roles', label: 'pages.settings.nav.roles', permission: 'roles.manage' },
  { id: 'jobTitles', label: 'pages.settings.nav.jobTitles', permission: 'members.manage' },
  { id: 'departments', label: 'pages.settings.nav.departments', permission: 'members.manage' },
  { id: 'workingDays', label: 'pages.settings.nav.workingDays', permission: 'organization.manage' },
  { id: 'sprints', label: 'pages.settings.nav.sprints', permission: 'sprints.manage' },
  { id: 'mail', label: 'pages.settings.nav.mail', permission: 'organization.manage' },
  { id: 'eventPlaces', label: 'pages.settings.nav.eventPlaces', permission: 'organization.manage' },
  { id: 'taskTags', label: 'pages.settings.nav.taskTags', permission: 'organization.manage' },
  { id: 'memberTypes', label: 'pages.settings.nav.memberTypes', permission: 'members.manage' },
  { id: 'contractTypes', label: 'pages.settings.nav.contractTypes', permission: 'members.manage' }
];

/** The settings sections a user may open. */
export const settingsSectionsFor = (user: SessionUser | null | undefined) => SETTINGS_SECTIONS.filter((s) => !s.permission || can(user, s.permission));

/** The platform's settings; the first is the default. */
export const PLATFORM_SETTINGS_SECTIONS = [
  { id: 'overview', label: 'platform.pages.nav.overview' },
  { id: 'details', label: 'organization.platformTitle' },
  { id: 'tax', label: 'platform.pages.nav.tax' },
  { id: 'smtp', label: 'platform.pages.nav.smtp' },
  { id: 'outbox', label: 'platform.pages.nav.outbox' },
  { id: 'isolation', label: 'platform.pages.nav.isolation' }
] as const satisfies readonly SettingsSection[];

/** Breadcrumb nav key per route prefix. */
export function crumbFor(pathname: string): NavKey {
  if (pathname.startsWith('/projects/')) return 'projects';
  const all: NavItem[] = [
    ...NAV,
    ...PLATFORM_NAV,
    { key: 'settings', to: '/settings', icon: 'settings' }
  ];
  return (
    all.find((n) => pathname === n.to || pathname.startsWith(n.to + '/'))
      ?.key ?? 'dashboard'
  );
}
